import { doc, setDoc, getDoc, collection, getDocs, updateDoc, deleteDoc, Timestamp, query, where } from "firebase/firestore";
import { db } from "../firebase";

class Goal {
  constructor(userId, data) {
    this.userId = userId;
    this.title = data.title;
    this.description = data.description || "";
    this.category = data.category || "other"; // "strength" | "endurance" | "skill" | "other"
    this.targetValue = data.targetValue;
    this.targetUnit = data.targetUnit;
    this.targetReps = data.targetReps || null; // For strength goals
    this.direction = data.direction || "increase"; // "increase" | "decrease"
    this.deadline = data.deadline;
    this.startDate = data.startDate || Timestamp.now();
    this.startingValue = data.startingValue || null;
    this.status = data.status || "active"; // "active" | "completed" | "abandoned"
    this.completedAt = data.completedAt || null;
    this.img = data.img || null;
    this.imgThumb = data.imgThumb || null;
    this.imgCredit = data.imgCredit || null;
    this.createdAt = data.createdAt || Timestamp.now();
    this.updatedAt = data.updatedAt || Timestamp.now();
  }

  static makeWebSafeId(title) {
    return title
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "")
      .substring(0, 50) + "-" + Date.now();
  }

  static getCollectionPath(userId) {
    return `goals/${userId}/items`;
  }

  static getDocPath(userId, goalId) {
    return `goals/${userId}/items/${goalId}`;
  }

  // Create a new goal
  static async create(userId, data) {
    const goalId = Goal.makeWebSafeId(data.title);
    const goalRef = doc(db, Goal.getDocPath(userId, goalId));

    const goalData = {
      title: data.title,
      description: data.description || "",
      category: data.category || "other",
      targetValue: data.targetValue,
      targetUnit: data.targetUnit,
      targetReps: data.targetReps || null,
      direction: data.direction || "increase",
      deadline: data.deadline,
      startDate: Timestamp.now(),
      startingValue: data.startingValue || null,
      status: "active",
      completedAt: null,
      img: data.img || null,
      imgThumb: data.imgThumb || null,
      imgCredit: data.imgCredit || null,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    await setDoc(goalRef, goalData);
    return goalId;
  }

  // Get a single goal by ID
  static async getById(userId, goalId) {
    const goalRef = doc(db, Goal.getDocPath(userId, goalId));
    const goalDoc = await getDoc(goalRef);

    if (!goalDoc.exists()) {
      return null;
    }

    return { id: goalDoc.id, ...goalDoc.data() };
  }

  // Get all goals for a user
  static async getAll(userId) {
    const goalsRef = collection(db, Goal.getCollectionPath(userId));
    const snapshot = await getDocs(goalsRef);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  // Get active goals
  static async getActive(userId) {
    const goalsRef = collection(db, Goal.getCollectionPath(userId));
    const q = query(goalsRef, where("status", "==", "active"));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  // Update goal status
  static async updateStatus(userId, goalId, status) {
    const goalRef = doc(db, Goal.getDocPath(userId, goalId));
    const updateData = {
      status,
      updatedAt: Timestamp.now(),
    };

    if (status === "completed") {
      updateData.completedAt = Timestamp.now();
    }

    await updateDoc(goalRef, updateData);
  }

  // Update goal
  static async update(userId, goalId, data) {
    const goalRef = doc(db, Goal.getDocPath(userId, goalId));
    await updateDoc(goalRef, {
      ...data,
      updatedAt: Timestamp.now(),
    });
  }

  // Delete goal
  static async delete(userId, goalId) {
    const goalRef = doc(db, Goal.getDocPath(userId, goalId));
    await deleteDoc(goalRef);
  }

  // Calculate progress percentage
  static calculateProgress(goal, currentValue) {
    if (!goal.startingValue && goal.direction === "increase") {
      // No baseline, use 0 as start
      return Math.min(100, (currentValue / goal.targetValue) * 100);
    }

    if (goal.direction === "increase") {
      const totalGain = goal.targetValue - goal.startingValue;
      const currentGain = currentValue - goal.startingValue;
      return Math.min(100, Math.max(0, (currentGain / totalGain) * 100));
    } else {
      // Decrease goal (lower is better)
      const totalDrop = goal.startingValue - goal.targetValue;
      const currentDrop = goal.startingValue - currentValue;
      return Math.min(100, Math.max(0, (currentDrop / totalDrop) * 100));
    }
  }

  // Calculate expected progress at current date
  static calculateExpectedProgress(goal) {
    const now = new Date();
    const startDate = goal.startDate.toDate ? goal.startDate.toDate() : new Date(goal.startDate);
    const deadline = goal.deadline.toDate ? goal.deadline.toDate() : new Date(goal.deadline);

    const totalDays = (deadline - startDate) / (1000 * 60 * 60 * 24);
    const elapsedDays = (now - startDate) / (1000 * 60 * 60 * 24);

    return Math.min(100, Math.max(0, (elapsedDays / totalDays) * 100));
  }

  // Calculate expected value at current date
  static calculateExpectedValue(goal) {
    const expectedProgress = Goal.calculateExpectedProgress(goal) / 100;
    const startValue = goal.startingValue || 0;

    if (goal.direction === "increase") {
      return startValue + (goal.targetValue - startValue) * expectedProgress;
    } else {
      return startValue - (startValue - goal.targetValue) * expectedProgress;
    }
  }

  // Get pace status
  static getPaceStatus(goal, currentValue) {
    const actualProgress = Goal.calculateProgress(goal, currentValue);
    const expectedProgress = Goal.calculateExpectedProgress(goal);

    const diff = actualProgress - expectedProgress;

    if (diff > 10) return { status: "ahead", label: "Ahead", color: "text-green-500", bgColor: "bg-green-100 dark:bg-green-900" };
    if (diff < -10) return { status: "behind", label: "Behind", color: "text-red-500", bgColor: "bg-red-100 dark:bg-red-900" };
    return { status: "on-track", label: "On Track", color: "text-blue-500", bgColor: "bg-blue-100 dark:bg-blue-900" };
  }

  // Calculate days remaining
  static getDaysRemaining(goal) {
    const now = new Date();
    const deadline = goal.deadline.toDate ? goal.deadline.toDate() : new Date(goal.deadline);
    const diff = deadline - now;
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }
}

export default Goal;
